import type { EventDayBasedBillableItem } from '@racemap/sdk/schema/billing';
import type { AddOns } from '@racemap/sdk/schema/billing/AddOns';
import { produce } from 'immer';
import { DateTime } from 'luxon';
import type { EventCycle } from '../billing';
import { sortBillableItems } from './sortBillableItems';

/**
 * Calculates the number of event cycles based on the provided billable event days.
 * Each event cycle represents a billing cycle.
 *
 * @param billableEventDays - An array of billable event days.
 * @param daysPerBillingCycle - The number of days per billing cycle. Defaults to DAYS_PER_BILLING_CYCLE.
 * @param startDate - The start date of the billing cycle. Defaults to the first day of the first billable event.
 * @param endDate - The end date of the billing cycle. Defaults to the last day of the last billable event.
 * @returns The number of event cycles.
 */

export function getEventCyclesOfTimeRange(
  billableItems: Array<EventDayBasedBillableItem>,
  daysPerBillingCycle: number,
  startDate?: Date,
  endDate?: Date,
): EventCycleInfos {
  const sortedBillableItems = sortBillableItems(billableItems);
  const out: EventCycleInfos = {
    eventCycles: [],
    totalCycles: 0,
  };
  if (sortedBillableItems.length === 0) return out;

  const start = DateTime.fromJSDate(startDate || new Date(sortedBillableItems[0].timeKey)).startOf(
    'day',
  );
  const end = DateTime.fromJSDate(
    endDate || new Date(sortedBillableItems[sortedBillableItems.length - 1].timeKey),
  ).endOf('day');
  let lastRunningBillingCycleStartDate: null | DateTime = null;

  for (const billableItem of sortedBillableItems) {
    const timeKey = DateTime.fromISO(billableItem.timeKey, { zone: 'utc' });
    const lastBillingCycle =
      out.eventCycles.length > 0 ? out.eventCycles[out.eventCycles.length - 1] : null;
    const endOfRunningBillingCycle = lastRunningBillingCycleStartDate?.plus({
      days: daysPerBillingCycle,
    });

    // update a existing billing event
    if (
      out.eventCycles.some((bC) => bC.billingEvents.some((e) => e.timeKey === billableItem.timeKey))
    ) {
      const billingEvent =
        out.eventCycles[
          out.eventCycles.findIndex((bC) =>
            bC.billingEvents.some((e) => e.timeKey === billableItem.timeKey),
          )
        ];
      const lastEventCycle = out.eventCycles[out.eventCycles.length - 1];
      billingEvent.activatedAddons = mergeActivatedAddons(
        lastEventCycle.activatedAddons,
        billableItem.activatedAddons,
      );

      const indexOfBillableEvent = billingEvent.billingEvents.findIndex(
        (e) => e.timeKey === billableItem.timeKey,
      );
      const existingBillableEvent = billingEvent.billingEvents[indexOfBillableEvent];
      billingEvent.billingEvents[indexOfBillableEvent] = produce(existingBillableEvent, (draft) => {
        return {
          ...draft,
          ...billableItem,
          activatedAddons: mergeActivatedAddons(
            existingBillableEvent.activatedAddons,
            billableItem.activatedAddons,
          ),
        };
      });
      continue;
    }

    if (endOfRunningBillingCycle != null && endOfRunningBillingCycle > timeKey) {
      // running billing cycle
      if (lastBillingCycle != null && DateTime.fromJSDate(lastBillingCycle.endDate) >= timeKey) {
        const lastEventCycle = out.eventCycles[out.eventCycles.length - 1];
        // if there is already a billing cycle, add the event to the last billing cycle
        lastEventCycle.billingEvents.push(billableItem);
        lastEventCycle.activatedAddons = mergeActivatedAddons(
          lastEventCycle.activatedAddons,
          billableItem.activatedAddons,
        );
      }
      continue;
    }

    if (timeKey >= start && timeKey <= end) {
      out.eventCycles.push({
        startDate: timeKey.startOf('day').toJSDate(),
        endDate: timeKey
          .plus({ days: daysPerBillingCycle - 1 })
          .endOf('day')
          .toJSDate(),
        startTimeKey: timeKey.startOf('day').toFormat('yyyy-MM-dd'),
        endTimeKey: timeKey
          .plus({ days: daysPerBillingCycle - 1 })
          .endOf('day')
          .toFormat('yyyy-MM-dd'),
        billingEvents: [billableItem],
        activatedAddons: billableItem.activatedAddons || {},
        eventId: billableItem.eventId,
      });
    }
    lastRunningBillingCycleStartDate = DateTime.fromISO(billableItem.timeKey, { zone: 'utc' });
    out.totalCycles++;
  }

  return out;
}

export interface EventCycleInfos {
  eventCycles: Array<EventCycle>;
  totalCycles: number;
}

const mergeActivatedAddons = (
  existingAddons?: Partial<Record<AddOns, number>>,
  newAddons?: Partial<Record<AddOns, number>>,
): Partial<Record<AddOns, number>> => {
  const mergedAddons: Partial<Record<AddOns, number>> = { ...existingAddons };
  if (newAddons == null) return mergedAddons;

  for (const [key, value] of Object.entries(newAddons)) {
    mergedAddons[key as AddOns] = Math.max(mergedAddons[key as AddOns] || 0, value || 0);
  }
  return mergedAddons;
};
